import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';

const shortenDescription = (description, maxLength = 96) => {
  if (!description) {
    return '';
  }

  const trimmed = String(description).trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }

  return `${trimmed.slice(0, maxLength)}...`;
};

const QuickJoinInvitePage = ({
  event,
  loading = false,
  message = '',
  nameMessage = '',
  onJoin,
  onLogin,
}) => {
  const [isNameFormVisible, setIsNameFormVisible] = useState(false);
  const [playerName, setPlayerName] = useState('');

  useEffect(() => {
    if (!nameMessage) {
      return;
    }

    setIsNameFormVisible(true);
  }, [nameMessage]);

  if (!event) {
    return null;
  }

  const description = shortenDescription(event.description);
  const trimmedPlayerName = playerName.trim();
  const canSubmitName = trimmedPlayerName.length > 0 && !loading;

  const handleNamedJoin = () => {
    if (!canSubmitName) {
      return;
    }

    onJoin?.(trimmedPlayerName);
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        boxSizing: 'border-box',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, sm: 7 },
        backgroundColor: '#f7f9fc',
      }}
    >
      <Stack
        spacing={3}
        sx={{
          width: 'min(100%, 560px)',
          mx: 'auto',
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 24, sm: 30 },
              fontWeight: 900,
              lineHeight: 1.35,
              color: '#1b1f2a',
            }}
          >
            Bettyのルームに招待されています
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: 15,
              lineHeight: 1.8,
              color: '#4b5563',
            }}
          >
            Bettyとはどんな話題も予想ゲームにできる無料のwebサービスです。
          </Typography>
        </Box>

        <Box
          sx={{
            borderTop: '1px solid #d9e0ea',
            borderBottom: '1px solid #d9e0ea',
            py: 2.5,
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: 20, sm: 22 },
              fontWeight: 800,
              lineHeight: 1.45,
              color: '#111827',
            }}
          >
            {event.name}
          </Typography>
          {event.owner_name && (
            <Typography sx={{ mt: 0.75, fontSize: 14, color: '#667085' }}>
              作成者: {event.owner_name}
            </Typography>
          )}
          {description && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 14,
                lineHeight: 1.75,
                color: '#374151',
                whiteSpace: 'pre-line',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {message && <Alert severity="error">{message}</Alert>}

        <Stack spacing={1.25}>
          {!isNameFormVisible && (
            <>
              <Button
                variant="contained"
                size="large"
                onClick={() => onJoin?.()}
                disabled={loading}
                sx={{
                  minHeight: 48,
                  fontWeight: 800,
                  boxShadow: 'none',
                }}
              >
                {loading ? '参加中...' : 'すぐに参加する'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => setIsNameFormVisible(true)}
                disabled={loading}
                sx={{
                  minHeight: 44,
                  fontWeight: 700,
                }}
              >
                名前を決めて参加する
              </Button>
            </>
          )}

          {isNameFormVisible && (
            <Box
              sx={{
                p: 2,
                border: '1px solid #d9e0ea',
                backgroundColor: '#fff',
              }}
            >
              <Stack spacing={1.5}>
                <Typography sx={{ fontSize: 14, color: '#374151' }}>
                  あなたの名前を教えてください
                </Typography>
                <TextField
                  label="Bettyでのあなたの名前を入力してください"
                  variant="outlined"
                  value={playerName}
                  onChange={(event) => setPlayerName(event.target.value)}
                  inputProps={{ maxLength: 10 }}
                  autoFocus
                  fullWidth
                  disabled={loading}
                />
                {nameMessage && <Alert severity="error">{nameMessage}</Alert>}
                <Stack direction="row" spacing={1.25} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => setIsNameFormVisible(false)}
                    disabled={loading}
                  >
                    戻る
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNamedJoin}
                    disabled={!canSubmitName}
                    sx={{ boxShadow: 'none' }}
                  >
                    {loading ? '参加中...' : '決定'}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          <Button
            variant="outlined"
            size="large"
            onClick={onLogin}
            disabled={loading}
            sx={{
              minHeight: 44,
              fontWeight: 700,
            }}
          >
            ログイン
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default QuickJoinInvitePage;
